import React from 'react';
import {
  Modal,
  Button,
  Card,
  Col,
  Empty,
  message,
  Row,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  runtimeOperationalService,
  runtimeDiagnosticsService,
  runtimeHistoryService,
  runtimeSchedulerService,
} from '../../services/runtimeOperationalService';
import { runtimeQueryKeys } from '../../services/runtimeQueryKeys';
import useRuntimeQuery from '../../hooks/useRuntimeQuery';
import useRuntimeMutation from '../../hooks/useRuntimeMutation';
import useRuntimeAutoRefresh from '../../hooks/useRuntimeAutoRefresh';
import useRuntimeActionLock from '../../hooks/useRuntimeActionLock';
import PlatformRuntimeRealChargeTester from './PlatformRuntimeRealChargeTester';
import {
  RuntimeCorrelationChip,
  RuntimeEmptyState,
  RuntimeErrorState,
  RuntimeLoadingState,
} from '../common/runtime';
import { buildApiErrorMessage } from '../../utils/apiError';

const { Title, Text } = Typography;

const HEALTH_COLOR = {
  healthy: 'green',
  lagging: 'gold',
  lock_contention: 'orange',
  stale_runs_detected: 'red',
};

const STATUS_COLOR = {
  completed: 'green',
  success: 'green',
  running: 'processing',
  pending: 'gold',
  failed: 'red',
  error: 'red',
  cancelled: 'default',
};

const SOURCE_COLOR = {
  scheduler: 'blue',
  lifecycle: 'cyan',
  webhook: 'purple',
  retry: 'orange',
  manual: 'default',
};

const ORIGIN_LABEL = {
  scheduler: 'Scheduler',
  lifecycle: 'Lifecycle',
  webhook: 'Webhook Bridge',
  retry: 'Retry',
  manual: 'Manual/Outras',
};

const AUTO_REFRESH_INTERVAL_MS = 60000;

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString('pt-BR');
};

const formatDuration = (value) => {
  if (value === null || value === undefined) {
    return '-';
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '-';
  }
  return `${numeric} ms`;
};

const statusTag = (value) => {
  const normalized = String(value || '').toLowerCase();
  const color = STATUS_COLOR[normalized] || 'default';
  return <Tag color={color}>{String(value || '-').toUpperCase()}</Tag>;
};

const healthTag = (value) => {
  const normalized = String(value || '').toLowerCase();
  const color = HEALTH_COLOR[normalized] || 'default';
  return <Tag color={color}>{String(value || '-').toUpperCase()}</Tag>;
};

const classifyRuntimeExecution = (row) => {
  const operationType = String(row?.operation_type || '').toLowerCase();

  if (operationType.includes('scheduler')) {
    return 'scheduler';
  }
  if (operationType.includes('lifecycle') || operationType.includes('materialize')) {
    return 'lifecycle';
  }
  if (operationType.includes('webhook_bridge')) {
    return 'webhook';
  }
  if (operationType.includes('retry')) {
    return 'retry';
  }
  return 'manual';
};

const ExecutionTable = ({
  title,
  data,
  loading,
  isError,
  error,
  onRetry,
  navigate,
  emptyDescription,
  showRetryPreview = false,
}) => {
  const apiError = isError
    ? buildApiErrorMessage(error, `Nao foi possivel carregar ${title.toLowerCase()}.`)
    : null;

  const columns = [
    {
      title: 'Operacao',
      dataIndex: 'operation_type',
      key: 'operation_type',
      render: (value, record) => (
        <Space size={6} wrap>
          <Text strong>{value || '-'}</Text>
          <Tag color={SOURCE_COLOR[record.origin] || 'default'}>{ORIGIN_LABEL[record.origin]}</Tag>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value) => statusTag(value),
    },
    {
      title: 'Contexto',
      key: 'context',
      render: (_, record) => (
        <Space size={6} wrap>
          {record.source ? <Tag>{`origem: ${String(record.source).toLowerCase()}`}</Tag> : null}
          {record.origin === 'lifecycle' ? <Tag color="cyan">lifecycle</Tag> : null}
          {record.dry_run === true ? <Tag color="blue">dry-run</Tag> : null}
          {record.has_lock_contention_signal ? <Tag color="orange">lock_busy</Tag> : null}
          {record.has_stale_signal ? <Tag color="red">stale</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Correlation ID',
      dataIndex: 'correlation_id',
      key: 'correlation_id',
      render: (value) => <RuntimeCorrelationChip correlationId={value} />,
    },
    {
      title: 'Inicio',
      dataIndex: 'started_at',
      key: 'started_at',
      render: (value) => formatDateTime(value),
    },
    {
      title: 'Duracao',
      dataIndex: 'duration_ms',
      key: 'duration_ms',
      render: (value) => formatDuration(value),
    },
    {
      title: 'Acoes',
      key: 'actions',
      render: (_, record) => (
        <Space size={8}>
          <Button
            size="small"
            type="link"
            onClick={() =>
              navigate(
                `/platform/saas-billing/runtime/history/executions/${encodeURIComponent(
                  String(record.execution_id || '')
                )}`
              )
            }
          >
            Ver detalhe
          </Button>
          {showRetryPreview ? (
            <Button
              size="small"
              type="link"
              onClick={() =>
                navigate(
                  `/platform/saas-billing/runtime/executions/${encodeURIComponent(
                    String(record.execution_id || '')
                  )}/retry-preview`
                )
              }
            >
              Preview Retry
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <Card title={title}>
      {loading ? <RuntimeLoadingState tip={`Carregando ${title.toLowerCase()}...`} /> : null}
      {!loading && isError ? (
        <RuntimeErrorState
          title={`Erro em ${title}`}
          description={apiError?.message || 'Falha inesperada.'}
          onRetry={onRetry}
        />
      ) : null}
      {!loading && !isError && (!Array.isArray(data) || data.length === 0) ? (
        <RuntimeEmptyState title="Sem registros" description={emptyDescription} />
      ) : null}
      {!loading && !isError && Array.isArray(data) && data.length > 0 ? (
        <Table rowKey="execution_id" columns={columns} dataSource={data} pagination={false} />
      ) : null}
    </Card>
  );
};

const buildCriticalEvents = ({ schedulerRuns, recentExecutions }) => {
  const lockBusyFromRuns = schedulerRuns.filter((row) => row.has_lock_contention_signal);
  const staleFromRuns = schedulerRuns.filter((row) => row.has_stale_signal);

  const lockBusyFromSummary = recentExecutions.filter((row) =>
    Boolean(row?.summary_json && typeof row.summary_json === 'object' && row.summary_json.lock_busy)
  );

  return {
    lockBusyCount: lockBusyFromRuns.length + lockBusyFromSummary.length,
    staleSignalCount: staleFromRuns.length,
    lockBusyExecutions: [...lockBusyFromRuns, ...lockBusyFromSummary],
    staleExecutions: staleFromRuns,
  };
};

const asObject = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return value;
};

const toNumeric = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const readCounter = (source, keys) => {
  const safeSource = asObject(source);
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(safeSource, key)) {
      return toNumeric(safeSource[key]);
    }
  }
  return 0;
};

const resolveLockBusy = (source) => {
  const safeSource = asObject(source);
  const raw = safeSource.lock_busy ?? safeSource.lock_contention ?? safeSource.has_lock_busy;
  if (typeof raw === 'boolean') {
    return raw;
  }
  return toNumeric(raw) > 0;
};

const parseLifecycleCounters = (source) => {
  const safeSource = asObject(source);
  const keys = Object.keys(safeSource);
  if (keys.length === 0) {
    return null;
  }

  return {
    due: readCounter(safeSource, ['due', 'due_count', 'total_due', 'pending_due']),
    processed: readCounter(safeSource, ['processed', 'processed_count', 'applied', 'effectuated']),
    failed: readCounter(safeSource, ['failed', 'failed_count', 'errors', 'error_count']),
    noop: readCounter(safeSource, ['noop', 'noop_count', 'no_op', 'no_op_count', 'skipped']),
    revoked: readCounter(safeSource, ['revoked', 'revoked_count']),
    lock_busy: resolveLockBusy(safeSource),
    hasAnyData: true,
  };
};

const resolveLifecycleSchedulerSummary = (executions) => {
  if (!Array.isArray(executions) || executions.length === 0) {
    return {
      scheduled_downgrade: null,
      scheduled_cancel: null,
      observed_at: null,
      hasData: false,
    };
  }

  const sorted = [...executions].sort((left, right) => {
    const leftTime = new Date(left?.started_at || 0).getTime();
    const rightTime = new Date(right?.started_at || 0).getTime();
    return rightTime - leftTime;
  });

  for (const execution of sorted) {
    const summaryJson = asObject(execution?.summary_json);
    const scheduledDowngrade = parseLifecycleCounters(summaryJson.scheduled_downgrade);
    const scheduledCancel = parseLifecycleCounters(summaryJson.scheduled_cancel);

    if (scheduledDowngrade || scheduledCancel) {
      return {
        scheduled_downgrade: scheduledDowngrade,
        scheduled_cancel: scheduledCancel,
        observed_at: execution?.started_at || null,
        hasData: true,
      };
    }
  }

  return {
    scheduled_downgrade: null,
    scheduled_cancel: null,
    observed_at: null,
    hasData: false,
  };
};

const LifecycleCounterCard = ({ title, data }) => {
  if (!data) {
    return (
      <Card size="small" title={title}>
        <Empty description="Sem dados para este bloco no summary_json." />
      </Card>
    );
  }

  return (
    <Card size="small" title={title}>
      <Row gutter={[12, 12]}>
        <Col xs={12} sm={8} md={4}><Statistic title="due" value={Number(data.due || 0)} /></Col>
        <Col xs={12} sm={8} md={4}><Statistic title="processed" value={Number(data.processed || 0)} /></Col>
        <Col xs={12} sm={8} md={4}><Statistic title="failed" value={Number(data.failed || 0)} /></Col>
        <Col xs={12} sm={8} md={4}><Statistic title="noop" value={Number(data.noop || 0)} /></Col>
        <Col xs={12} sm={8} md={4}><Statistic title="revoked" value={Number(data.revoked || 0)} /></Col>
        <Col xs={12} sm={8} md={4}>
          <Statistic
            title="lock_busy"
            value={data.lock_busy ? 'SIM' : 'NAO'}
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
      </Row>
    </Card>
  );
};

const PlatformRuntimeCockpit = () => {
  const navigate = useNavigate();
  const { withActionLock, isLocked } = useRuntimeActionLock();
  const [autoRefreshEnabled, setAutoRefreshEnabled] = React.useState(true);
  const [lastRefreshAt, setLastRefreshAt] = React.useState(null);

  const lifecycleActionLockKey = 'runtime-cockpit:lifecycle-dry-run';

  const lifecycleDryRunMutation = useRuntimeMutation({
    mutationFn: async (payload) => {
      const response = await runtimeOperationalService.runLifecycleMaterializerDryRun(payload);
      return response.data;
    },
  });

  const metricsParams = React.useMemo(() => ({ older_than_hours: 24 }), []);
  const schedulerParams = React.useMemo(
    () => ({ recent_window_hours: 24, stale_run_seconds: 1800, lag_threshold_seconds: 1800 }),
    []
  );
  const schedulerRunsParams = React.useMemo(
    () => ({ recent_window_hours: 24, stale_run_seconds: 1800, limit: 8 }),
    []
  );
  const recentExecutionsParams = React.useMemo(() => ({ limit: 24, offset: 0 }), []);
  const recentFailuresParams = React.useMemo(() => ({ status: 'failed', limit: 8, offset: 0 }), []);

  const metricsQuery = useRuntimeQuery({
    queryKey: runtimeQueryKeys.diagnosticsMetrics(metricsParams),
    queryFn: async () => {
      const response = await runtimeDiagnosticsService.getMetrics(metricsParams);
      return response.data;
    },
  });

  const schedulerHealthQuery = useRuntimeQuery({
    queryKey: runtimeQueryKeys.schedulerHealth(schedulerParams),
    queryFn: async () => {
      const response = await runtimeSchedulerService.getHealth(schedulerParams);
      return response.data;
    },
  });

  const schedulerDiagnosticsQuery = useRuntimeQuery({
    queryKey: runtimeQueryKeys.schedulerDiagnostics(schedulerParams),
    queryFn: async () => {
      const response = await runtimeSchedulerService.getDiagnostics(schedulerParams);
      return response.data;
    },
  });

  const schedulerRunsQuery = useRuntimeQuery({
    queryKey: runtimeQueryKeys.schedulerRuns(schedulerRunsParams),
    queryFn: async () => {
      const response = await runtimeSchedulerService.getRuns(schedulerRunsParams);
      return response.data;
    },
  });

  const recentExecutionsQuery = useRuntimeQuery({
    queryKey: runtimeQueryKeys.executionsList(recentExecutionsParams),
    queryFn: async () => {
      const response = await runtimeHistoryService.listExecutions(recentExecutionsParams);
      return response.data;
    },
  });

  const recentFailuresQuery = useRuntimeQuery({
    queryKey: runtimeQueryKeys.executionsList(recentFailuresParams),
    queryFn: async () => {
      const response = await runtimeHistoryService.listExecutions(recentFailuresParams);
      return response.data;
    },
  });

  const refreshAll = React.useCallback(async () => {
    await Promise.allSettled([
      metricsQuery.refetch(),
      schedulerHealthQuery.refetch(),
      schedulerDiagnosticsQuery.refetch(),
      schedulerRunsQuery.refetch(),
      recentExecutionsQuery.refetch(),
      recentFailuresQuery.refetch(),
    ]);
    setLastRefreshAt(new Date().toISOString());
  }, [
    metricsQuery,
    schedulerHealthQuery,
    schedulerDiagnosticsQuery,
    schedulerRunsQuery,
    recentExecutionsQuery,
    recentFailuresQuery,
  ]);

  useRuntimeAutoRefresh({
    enabled: autoRefreshEnabled,
    intervalMs: AUTO_REFRESH_INTERVAL_MS,
    pauseWhenHidden: true,
    jitterRatio: 0.1,
    onRefresh: refreshAll,
  });

  const isBootLoading =
    metricsQuery.isLoading &&
    schedulerHealthQuery.isLoading &&
    schedulerDiagnosticsQuery.isLoading &&
    recentExecutionsQuery.isLoading;

  const hasCriticalError =
    metricsQuery.isError &&
    schedulerHealthQuery.isError &&
    schedulerDiagnosticsQuery.isError &&
    recentExecutionsQuery.isError;

  const criticalError = hasCriticalError
    ? buildApiErrorMessage(metricsQuery.error, 'Nao foi possivel carregar o Runtime Cockpit.')
    : null;

  const schedulerHealth = schedulerHealthQuery.data || null;
  const schedulerDiagnostics = schedulerDiagnosticsQuery.data || null;
  const metrics = metricsQuery.data || null;

  const schedulerRunsRaw = Array.isArray(schedulerRunsQuery.data?.runs)
    ? schedulerRunsQuery.data.runs
    : [];
  const recentExecutionsRaw = Array.isArray(recentExecutionsQuery.data?.items)
    ? recentExecutionsQuery.data.items
    : [];
  const recentFailuresRaw = Array.isArray(recentFailuresQuery.data?.items)
    ? recentFailuresQuery.data.items
    : [];

  const schedulerSignalsByExecutionId = React.useMemo(() => {
    const signals = {};
    schedulerRunsRaw.forEach((row) => {
      const key = String(row?.execution_id || '').trim();
      if (!key) {
        return;
      }
      signals[key] = {
        has_stale_signal: Boolean(row?.has_stale_signal),
        has_lock_contention_signal: Boolean(row?.has_lock_contention_signal),
      };
    });
    return signals;
  }, [schedulerRunsRaw]);

  const enrichExecution = React.useCallback(
    (row) => {
      const executionId = String(row?.execution_id || '').trim();
      const schedulerSignal = schedulerSignalsByExecutionId[executionId] || {};
      return {
        ...row,
        origin: classifyRuntimeExecution(row),
        has_stale_signal:
          Boolean(schedulerSignal.has_stale_signal) ||
          Boolean(row?.summary_json && typeof row.summary_json === 'object' && row.summary_json.stale_count > 0),
        has_lock_contention_signal:
          Boolean(schedulerSignal.has_lock_contention_signal) ||
          Boolean(row?.summary_json && typeof row.summary_json === 'object' && row.summary_json.lock_busy),
      };
    },
    [schedulerSignalsByExecutionId]
  );

  const recentExecutions = React.useMemo(
    () => recentExecutionsRaw.map((row) => enrichExecution(row)),
    [recentExecutionsRaw, enrichExecution]
  );

  const recentFailures = React.useMemo(
    () => recentFailuresRaw.map((row) => enrichExecution(row)),
    [recentFailuresRaw, enrichExecution]
  );

  const schedulerRuns = React.useMemo(
    () =>
      schedulerRunsRaw.map((row) => ({
        execution_id: row.execution_id,
        correlation_id: row.correlation_id,
        operation_type: row.operation_type,
        status: row.status,
        source: 'scheduler',
        dry_run: true,
        started_at: row.started_at,
        duration_ms: row.duration_ms,
        origin: 'scheduler',
        has_stale_signal: Boolean(row.has_stale_signal),
        has_lock_contention_signal: Boolean(row.has_lock_contention_signal),
      })),
    [schedulerRunsRaw]
  );

  const groupedByOrigin = React.useMemo(() => {
    return recentExecutions.reduce(
      (acc, row) => {
        const group = acc[row.origin] || [];
        group.push(row);
        acc[row.origin] = group;
        return acc;
      },
      {
        scheduler: [],
        lifecycle: [],
        webhook: [],
        retry: [],
        manual: [],
      }
    );
  }, [recentExecutions]);

  const hasDryRunEvidence = React.useMemo(
    () => recentExecutions.some((row) => row.dry_run === true),
    [recentExecutions]
  );

  const latestSchedulerExecution = React.useMemo(() => {
    if (schedulerRuns.length > 0) {
      return schedulerRuns[0];
    }
    return groupedByOrigin.scheduler[0] || null;
  }, [schedulerRuns, groupedByOrigin.scheduler]);

  const criticalSignals = React.useMemo(
    () => buildCriticalEvents({ schedulerRuns, recentExecutions }),
    [schedulerRuns, recentExecutions]
  );

  const diagnosticsGeneratedAt =
    schedulerDiagnostics?.generated_at || schedulerHealth?.generated_at || metrics?.generated_at || null;

  const nextEstimatedExecutionAt = React.useMemo(() => {
    const base = schedulerHealth?.metrics?.last_execution_at;
    if (!base) {
      return null;
    }
    const parsed = new Date(base);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return new Date(parsed.getTime() + AUTO_REFRESH_INTERVAL_MS).toISOString();
  }, [schedulerHealth?.metrics?.last_execution_at]);

  const lifecycleSchedulerSummary = React.useMemo(
    () => resolveLifecycleSchedulerSummary(groupedByOrigin.scheduler),
    [groupedByOrigin.scheduler]
  );

  const runLifecycleDryRun = React.useCallback(async () => {
    const run = await withActionLock(lifecycleActionLockKey, async () => {
      const result = await lifecycleDryRunMutation.mutateAsync({ dry_run: true });
      await refreshAll();

      const executionId = String(result?.execution_id || '-');
      const plannedCount = Number(result?.summary?.planned_count || 0);
      const skippedCount = Number(result?.summary?.skipped_count || 0);

      message.success(
        `Lifecycle dry-run concluido. execution_id=${executionId}; planned=${plannedCount}; skipped=${skippedCount}`
      );

      return result;
    });

    if (run?.skipped) {
      return;
    }
  }, [withActionLock, lifecycleDryRunMutation, refreshAll]);

  const confirmRunLifecycleDryRun = React.useCallback(() => {
    Modal.confirm({
      title: 'Execute lifecycle materializer dry-run?',
      content: 'No tenant/subscription state will be mutated.',
      okText: 'Executar dry-run',
      cancelText: 'Cancelar',
      okButtonProps: {
        loading: lifecycleDryRunMutation.isPending,
      },
      onOk: async () => {
        try {
          await runLifecycleDryRun();
        } catch (error) {
          const apiError = buildApiErrorMessage(error, 'Falha ao executar lifecycle materializer dry-run.');
          message.error(apiError.message);
        }
      },
    });
  }, [runLifecycleDryRun, lifecycleDryRunMutation.isPending]);

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <Title level={2} style={{ marginTop: 0, marginBottom: 4 }}>
            Runtime Cockpit
          </Title>
          <Text type="secondary">
            Visao operacional do SaaS Billing Runtime para monitoramento diario.
          </Text>
        </div>

        <Card>
          <Space wrap size={12}>
            <Button onClick={() => navigate('/platform/saas-billing/runtime/history')}>
              Abrir Runtime History
            </Button>
            <Button onClick={() => navigate('/platform/saas-billing/runtime/search')}>
              Abrir Runtime Search
            </Button>
            <Button
              type="primary"
              onClick={confirmRunLifecycleDryRun}
              loading={lifecycleDryRunMutation.isPending}
              disabled={isLocked(lifecycleActionLockKey)}
            >
              Run Lifecycle Dry-Run
            </Button>
            <Tag color="blue">DRY-RUN</Tag>
            <Text type="secondary">No tenant/subscription state will be mutated.</Text>
            <Button
              onClick={refreshAll}
              loading={
                metricsQuery.isFetching ||
                schedulerHealthQuery.isFetching ||
                schedulerDiagnosticsQuery.isFetching
              }
            >
              Atualizar agora
            </Button>
            <Space size={8}>
              <Text>Auto-refresh</Text>
              <Switch checked={autoRefreshEnabled} onChange={setAutoRefreshEnabled} />
              <Tag color={autoRefreshEnabled ? 'green' : 'default'}>
                {autoRefreshEnabled ? 'ATIVO' : 'PAUSADO'}
              </Tag>
              <Text type="secondary">60s</Text>
            </Space>
          </Space>
        </Card>

        <PlatformRuntimeRealChargeTester />

        {isBootLoading ? <RuntimeLoadingState tip="Carregando Runtime Cockpit..." /> : null}
        {!isBootLoading && hasCriticalError ? (
          <RuntimeErrorState
            title="Erro ao carregar Runtime Cockpit"
            description={criticalError?.message || 'Falha inesperada no carregamento do cockpit.'}
            onRetry={refreshAll}
          />
        ) : null}

        {!isBootLoading && !hasCriticalError ? (
          <>
            <Card title="Status Global">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                  <Statistic
                    title="Saude do scheduler"
                    value={String(schedulerHealth?.classification || 'unknown').toUpperCase()}
                    valueStyle={{ fontSize: 18 }}
                    suffix={healthTag(schedulerHealth?.classification)}
                  />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Statistic
                    title="Falhas recentes"
                    value={Number(schedulerHealth?.metrics?.failed_runs_count || 0)}
                  />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Statistic
                    title="Sinais stale"
                    value={Number(schedulerHealth?.metrics?.stale_runs_count || 0)}
                  />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Statistic
                    title="Lock contention"
                    value={Number(schedulerHealth?.metrics?.lock_contention_count || 0)}
                  />
                </Col>
              </Row>

              <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
                <Col xs={24} md={12} lg={8}>
                  <Text strong>Ultimo tick observado</Text>
                  <div>{formatDateTime(latestSchedulerExecution?.started_at || schedulerHealth?.metrics?.last_execution_at)}</div>
                </Col>
                <Col xs={24} md={12} lg={8}>
                  <Text strong>Proxima execucao estimada</Text>
                  <div>{formatDateTime(nextEstimatedExecutionAt)}</div>
                </Col>
                <Col xs={24} md={24} lg={8}>
                  <Text strong>Modo operacional</Text>
                  <div>
                    <Space wrap size={8}>
                      {hasDryRunEvidence ? <Tag color="blue">DRY-RUN IDENTIFICADO</Tag> : <Tag>sem evidencia de dry-run</Tag>}
                      <Tag color="default">
                        dados atualizados: {formatDateTime(lastRefreshAt || diagnosticsGeneratedAt)}
                      </Tag>
                    </Space>
                  </div>
                </Col>
              </Row>

              {Array.isArray(schedulerDiagnostics?.reason_codes) && schedulerDiagnostics.reason_codes.length > 0 ? (
                <Row style={{ marginTop: 12 }}>
                  <Col span={24}>
                    <Text type="secondary">
                      Motivos de classificacao: {schedulerDiagnostics.reason_codes.join(', ')}
                    </Text>
                  </Col>
                </Row>
              ) : null}
            </Card>

            <Card title="Execucoes por Origem">
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <ExecutionTable
                  title="Scheduler"
                  data={groupedByOrigin.scheduler}
                  loading={recentExecutionsQuery.isLoading || schedulerRunsQuery.isLoading}
                  isError={recentExecutionsQuery.isError}
                  error={recentExecutionsQuery.error}
                  onRetry={() => recentExecutionsQuery.refetch()}
                  navigate={navigate}
                  emptyDescription="Nenhuma execucao de scheduler encontrada no recorte atual."
                />

                <ExecutionTable
                  title="Lifecycle Operations"
                  data={groupedByOrigin.lifecycle}
                  loading={recentExecutionsQuery.isLoading}
                  isError={recentExecutionsQuery.isError}
                  error={recentExecutionsQuery.error}
                  onRetry={() => recentExecutionsQuery.refetch()}
                  navigate={navigate}
                  emptyDescription="Sem execucoes de lifecycle no periodo recente."
                />

                <ExecutionTable
                  title="Webhook Bridge"
                  data={groupedByOrigin.webhook}
                  loading={recentExecutionsQuery.isLoading}
                  isError={recentExecutionsQuery.isError}
                  error={recentExecutionsQuery.error}
                  onRetry={() => recentExecutionsQuery.refetch()}
                  navigate={navigate}
                  emptyDescription="Sem execucoes de webhook bridge no periodo recente."
                />

                <ExecutionTable
                  title="Retry"
                  data={groupedByOrigin.retry}
                  loading={recentExecutionsQuery.isLoading}
                  isError={recentExecutionsQuery.isError}
                  error={recentExecutionsQuery.error}
                  onRetry={() => recentExecutionsQuery.refetch()}
                  navigate={navigate}
                  showRetryPreview
                  emptyDescription="Sem execucoes de retry no periodo recente."
                />

                <ExecutionTable
                  title="Manual/Outras"
                  data={groupedByOrigin.manual}
                  loading={recentExecutionsQuery.isLoading}
                  isError={recentExecutionsQuery.isError}
                  error={recentExecutionsQuery.error}
                  onRetry={() => recentExecutionsQuery.refetch()}
                  navigate={navigate}
                  emptyDescription="Sem operacoes manuais/outras no recorte atual."
                />
              </Space>
            </Card>

            <Card title="Lifecycle Scheduler">
              {!lifecycleSchedulerSummary.hasData ? (
                <RuntimeEmptyState
                  title="Sem resumo de lifecycle no scheduler"
                  description="Nenhum summary_json recente com scheduled_downgrade ou scheduled_cancel foi encontrado."
                />
              ) : (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Text type="secondary">
                    Ultima observacao: {formatDateTime(lifecycleSchedulerSummary.observed_at)}
                  </Text>
                  <LifecycleCounterCard
                    title="scheduled_downgrade"
                    data={lifecycleSchedulerSummary.scheduled_downgrade}
                  />
                  <LifecycleCounterCard
                    title="scheduled_cancel"
                    data={lifecycleSchedulerSummary.scheduled_cancel}
                  />
                </Space>
              )}
            </Card>

            <Card title="Falhas e Eventos Criticos">
              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <Card size="small">
                    <Statistic title="Executions com falha" value={recentFailures.length} />
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card size="small">
                    <Statistic title="Eventos lock_busy" value={criticalSignals.lockBusyCount} />
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card size="small">
                    <Statistic title="Sinais stale" value={criticalSignals.staleSignalCount} />
                  </Card>
                </Col>
              </Row>

              <div style={{ marginTop: 16 }}>
                <ExecutionTable
                  title="Falhas recentes"
                  data={recentFailures}
                  loading={recentFailuresQuery.isLoading}
                  isError={recentFailuresQuery.isError}
                  error={recentFailuresQuery.error}
                  onRetry={() => recentFailuresQuery.refetch()}
                  navigate={navigate}
                  showRetryPreview
                  emptyDescription="Nenhuma falha recente. Operacao estavel neste momento."
                />
              </div>

              <div style={{ marginTop: 16 }}>
                <Card size="small" title="Eventos criticos observados">
                  {criticalSignals.lockBusyExecutions.length === 0 &&
                  criticalSignals.staleExecutions.length === 0 ? (
                    <Empty description="Sem lock contention ou stale signals no recorte atual." />
                  ) : (
                    <Space direction="vertical" size={10} style={{ width: '100%' }}>
                      {criticalSignals.lockBusyExecutions.map((row) => (
                        <Card key={`lock-${row.execution_id}`} size="small" style={{ background: '#fffbe6' }}>
                          <Space direction="vertical" size={4}>
                            <Text strong>Lock contention detectado</Text>
                            <Text>{String(row.operation_type || '-')}</Text>
                            <Text type="secondary">{formatDateTime(row.started_at)}</Text>
                            <RuntimeCorrelationChip correlationId={row.correlation_id} />
                          </Space>
                        </Card>
                      ))}
                      {criticalSignals.staleExecutions.map((row) => (
                        <Card key={`stale-${row.execution_id}`} size="small" style={{ background: '#fff1f0' }}>
                          <Space direction="vertical" size={4}>
                            <Text strong>Execucao com sinal stale</Text>
                            <Text>{String(row.operation_type || '-')}</Text>
                            <Text type="secondary">{formatDateTime(row.started_at)}</Text>
                            <RuntimeCorrelationChip correlationId={row.correlation_id} />
                          </Space>
                        </Card>
                      ))}
                    </Space>
                  )}
                </Card>
              </div>
            </Card>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Card>
                  <Statistic
                    title="Invoices sem charge"
                    value={Number(metrics?.data?.issue_counters?.invoices_without_charge || 0)}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Card>
                  <Statistic
                    title="Stale pending charges"
                    value={Number(metrics?.data?.issue_counters?.stale_pending_charges || 0)}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Card>
                  <Statistic
                    title="Tenants com falha"
                    value={Number(metrics?.data?.issue_counters?.tenants_with_failed_charges || 0)}
                  />
                </Card>
              </Col>
            </Row>
          </>
        ) : null}
      </Space>
    </div>
  );
};

export default PlatformRuntimeCockpit;
